import { useState } from 'react';
import styled from '@emotion/styled';
import { Button, Tabs } from 'antd';
import { useTranslate } from '@tolgee/react';
import useConfig from './hooks/useConfig';
import InsiteConfig from './InsiteConfig';
import OutsiteConfig from './OutsiteConfig';
import useModal from '../../../hooks/useModal';
import Tip from './components/Tip';
import { DingTalkFormValues } from './components/DingTalkForm';

const { TabPane } = Tabs;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  padding: 0 24px;
  background: var(--fett-bg-panel);
  border-bottom: solid 1px var(--fett-border-default);
  > div {
    flex: 1 0 0;
    display: flex;
    align-items: center;
    overflow-y: hidden;
  }
  .ant-tabs-nav {
    margin-bottom: 0;
    .ant-tabs-tab {
      padding: 17px 0;
    }
  }
`;

const Tips = styled.span`
  flex: 1 0 0;
  margin-left: 28px;
  font-size: 12px;
  color: var(--fett-text-tertiary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExtraRightContentWrapper = styled.div`
  flex: 0 0 auto !important;
  .remind-tab-config {
    cursor: pointer;
  }
`;

const TabInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  span {
    padding-right: 8px;
  }
`;

type TabType = 'inSite' | 'outSite';

interface RemindConfigProps {
  config: Remind.Config;
  onUpdated: () => Promise<any>;
}

function Remind(props: RemindConfigProps) {
  const { config, onUpdated } = props;

  const { taskId, dingtalkConfig, feishuConfig } = config;

  const { t } = useTranslate();

  const { updateInsiteEnable, updateInsiteScene, updateOutsite, updateDingTalk, deleteDingTalk } =
    useConfig({
      onUpdated,
    });

  const [activeTab, setActiveTab] = useState<TabType>('inSite');

  const modal = useModal<DingTalkFormValues>(
    false,
    dingtalkConfig
      ? { ...dingtalkConfig, type: 0, taskId }
      : feishuConfig
      ? { ...feishuConfig, type: 1, taskId }
      : undefined,
  );

  const tabs = (
    <Tabs activeKey={activeTab} onChange={(key: any) => setActiveTab(key)}>
      <TabPane tab={t('remind.站内提醒')} key="inSite" />
      <TabPane tab={t('remind.站外提醒')} key="outSite" />
    </Tabs>
  );

  const headerLeft = (
    <div>
      {tabs}
      {activeTab === 'inSite' && (
        <Tips>{t('remind.配置后，符合相应条件的会话，将对客服进行重要提醒')}</Tips>
      )}
    </div>
  );

  const dingTalkButton =
    activeTab === 'outSite' ? (
      dingtalkConfig ? (
        <Button type="text" onClick={() => modal.show({ ...dingtalkConfig, type: 0, taskId })}>
          {t('knowledge.编辑x', { x: t('remind.站外提醒') })}
        </Button>
      ) : feishuConfig ? (
        <Button type="text" onClick={() => modal.show({ ...feishuConfig, type: 1, taskId })}>
          {t('knowledge.编辑x', { x: t('remind.站外提醒') })}
        </Button>
      ) : null
    ) : null;

  const headerRight = (
    <ExtraRightContentWrapper>
      {dingTalkButton}
      <TabInfoWrapper>
        <span>{t('marketing.示例')}</span>
        <Tip />
      </TabInfoWrapper>
    </ExtraRightContentWrapper>
  );

  const header = (
    <Header>
      {headerLeft}
      {headerRight}
    </Header>
  );

  const inSite = (
    <InsiteConfig
      config={config}
      onUpdateInsiteEnable={updateInsiteEnable}
      onUpdateInsiteScene={updateInsiteScene}
    />
  );

  const outSite = (
    <OutsiteConfig
      config={config}
      onUpdateOutsite={updateOutsite}
      onUpdateDingTalk={updateDingTalk}
      onDeleteDingTalk={deleteDingTalk}
      modal={modal}
    />
  );

  return (
    <div>
      {header}
      {
        {
          inSite,
          outSite,
        }[activeTab]
      }
    </div>
  );
}

export default Remind;
